import {
  OKTAUserSeviceFlagEnum,
  OKTAUserTypeEnum,
} from "@app/hooks/useUserInfo";
import { LabelCardIconVariety } from "@elements/Icon/LabelCardIcon";
import hasServiceFlag from "@helper/hasServiceFlag";
import { getUserType } from "@src/app/hooks/useUserInfo.ts";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";

import { ArticleParagraphProps } from "../ArticleParagraph";

export function useAccessControl(
  props: Pick<
    ArticleParagraphProps,
    | "contentAccess"
    | "uniqueSectionName"
    | "isInfiniteScrollArticle"
    | "freeArticle"
  >,
) {
  const {
    contentAccess,
    uniqueSectionName,
    isInfiniteScrollArticle,
    freeArticle,
  } = props;
  const [showSubBanner, setShowSubBanner] = useState(false);
  const [showGenGSubBanner, setShowGenGSubBanner] = useState(false);
  const [showRegBanner, setShowRegBanner] = useState(false);
  const [showReg2Banner, setShowReg2Banner] = useState<boolean>(false);
  const [showNormalBanner, setShowNormalBanner] = useState(false);

  useEffect(() => {
    // Get the user type (e.g., y-sub, y-anoy, etc.)
    const userType = getUserType();
    const mysphidCookie = Cookies.get("mysph-sid");

    if (
      contentAccess === LabelCardIconVariety.FREE &&
      !mysphidCookie &&
      freeArticle?.variant === "partial"
    ) {
      setShowReg2Banner(true);
      // Condition 3: If the content requires a PREMIUM subscription,
      // the section is "gen-g", and the user doesn't have the "bh_geng" service flag,
      // show the Gen-G subscription banner and trim the article body to show only one paragraph.
    } else if (
      contentAccess === LabelCardIconVariety.PREMIUM &&
      uniqueSectionName === "gen-g" &&
      !hasServiceFlag(OKTAUserSeviceFlagEnum.BH_GENG)
    ) {
      setShowGenGSubBanner(true);

      // Condition 4: If the content requires a PREMIUM subscription,
      // but the user is not a "y-sub" type, show the generic subscription banner.
    } else if (
      contentAccess === LabelCardIconVariety.PREMIUM &&
      uniqueSectionName !== "gen-g" &&
      (userType !== OKTAUserTypeEnum.SUBSCRIBER ||
        !hasServiceFlag(OKTAUserSeviceFlagEnum.BH_ONLINE))
    ) {
      setShowSubBanner(true);

      // Condition 5: If the content is for REGISTERED_USER,
      // and the user is either anonymous ("y-anoy") or the user type is undefined,
      // show the registration banner.
    } else if (
      contentAccess === LabelCardIconVariety.REGISTERED_USER &&
      userType === OKTAUserTypeEnum.ANONYMOUS
    ) {
      setShowRegBanner(true);

      // Condition 6: For gen g ala carte cases where the user is not a "y-anoy" type
      // and has service flag bh_geng, show the normal banner and set the
      // full article body to be displayed.
    } else if (
      uniqueSectionName === "gen-g" &&
      userType !== OKTAUserTypeEnum.ANONYMOUS &&
      hasServiceFlag(OKTAUserSeviceFlagEnum.BH_GENG)
    ) {
      if (
        userType !== OKTAUserTypeEnum.SUBSCRIBER ||
        !hasServiceFlag(OKTAUserSeviceFlagEnum.BH_ONLINE)
      ) {
        setShowNormalBanner(true);
      }

      // Condition 7: For all other cases where the user is not a "y-sub" type,
      // show the normal banner and set the full article body to be displayed.
    } else if (
      userType !== OKTAUserTypeEnum.SUBSCRIBER ||
      !hasServiceFlag(OKTAUserSeviceFlagEnum.BH_ONLINE)
    ) {
      setShowNormalBanner(true);

      // Default: If none of the above conditions are met,
      // set the full article body to be displayed and hide all banners.
    } else {
      setShowGenGSubBanner(false);
      setShowSubBanner(false);
      setShowRegBanner(false);
      setShowNormalBanner(false);
      setShowReg2Banner(false);
    }
  }, [
    contentAccess,
    uniqueSectionName,
    isInfiniteScrollArticle,
    freeArticle?.variant,
  ]);

  return {
    showSubBanner,
    showGenGSubBanner,
    showRegBanner,
    showNormalBanner,
    showReg2Banner,
  };
}
